import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '@nextui-org/react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  type FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from 'react-error-boundary'
import { v4 as uuidv4 } from 'uuid'

dayjs.extend(utc)
dayjs.extend(timezone)

const errorId = uuidv4() // Generate a unique error ID
const errorDate = dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss')
const url = window.location.href

function fallbackRender({ error, resetErrorBoundary }: FallbackProps) {
  const handleReloadPage = () => {
    resetErrorBoundary()
    window.location.reload()
  }

  return (
    <div className="flex h-[60vh] items-center justify-center" role="alert">
      <div className="text-center">
        <ExclamationCircleIcon
          aria-hidden="true"
          className="mx-auto h-32 w-32 text-danger"
        />
        <h1 className="my-6 text-2xl font-medium">
          Oops... Something went wrong!
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Try refreshing the page or contact the support team if the problem
          persists.
        </p>
        <div className="mt-4 grid gap-1 border p-6 text-left text-xs text-gray-500">
          <span>Details:</span>
          <span>Error ID: {errorId}</span>
          <span>Error: {error}</span>
          <span>URL: {url}</span>
          <span>Date: {errorDate}</span>
        </div>
        <Button
          className="mt-8 uppercase"
          color="primary"
          onPress={handleReloadPage}
          variant="solid"
        >
          Refresh Page
        </Button>
      </div>
    </div>
  )
}

const logError = (error: Error, info: React.ErrorInfo) => {
  console.error('Error ID:', errorId)
  console.error('Error occurred at:', errorDate)
  console.error('Error message:', error.message)
  console.error('Component stack trace:', info.componentStack)
  console.error('URL:', url)
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ReactErrorBoundary
      fallbackRender={fallbackRender}
      onError={logError}
      onReset={(details) => {
        console.log('Error boundary reset:', details)
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}

export default ErrorBoundary
